import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './button';
import { Upload, X, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImageUpload = ({ 
  value, 
  onChange, 
  className = '',
  previewClassName = 'w-full h-32'
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Dovoljena so le slikovne datoteke (JPG, PNG, GIF, WEBP)');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Datoteka je prevelika. Največ 10MB.');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        // Use the full URL for the uploaded file
        const imageUrl = `${BACKEND_URL}${response.data.url}`;
        onChange(imageUrl);
        toast.success('Slika je bila naložena');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Napaka pri nalaganju slike');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  const handleClear = () => {
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Preview */}
      {value && (
        <div className={`relative rounded-lg overflow-hidden bg-stone-900 ${previewClassName}`}>
          <img 
            src={value} 
            alt="Preview" 
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <button
            onClick={handleClear}
            className="absolute top-2 right-2 p-1 bg-red-600 hover:bg-red-500 rounded-full text-white transition-colors"
            title="Odstrani sliko"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Upload Area */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer
          transition-colors duration-200
          ${dragOver 
            ? 'border-emerald-500 bg-emerald-500/10' 
            : 'border-stone-700 hover:border-stone-600 bg-stone-900/50'
          }
          ${uploading ? 'pointer-events-none opacity-50' : ''}
        `}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleInputChange}
          className="hidden"
        />
        
        {uploading ? (
          <div className="flex flex-col items-center gap-2 py-2">
            <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
            <span className="text-sm text-stone-400">Nalagam...</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 py-2">
            <Upload className="w-6 h-6 text-stone-500" />
            <span className="text-sm text-stone-400">
              Kliknite ali povlecite sliko sem
            </span>
            <span className="text-xs text-stone-600">
              JPG, PNG, GIF, WEBP (max 10MB)
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageUpload;
